import { Button, Divider, InputAdornment, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { filter, isEmpty, map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { currencyExchangeApi } from 'api';
import {
  DataWrapper,
  FormControls,
  FormikNumericField,
  FormikSelect,
  FormikTextField,
  FormikYesNoRadioGroup,
  InfoTooltip,
  PageHeader,
  CurrencyExchangeDirectionSelect,
  FormikMultiSelect,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import {
  CurrencyExchangeDirection,
  CurrencyExchangeSource,
  QueryKey,
} from 'enums';
import {
  useCrossFiatCurrencyExchange,
  useCurrencies,
  useMutation,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { CurrencyExchange } from 'types';
import { currencyExchangeUtils, validationUtils } from 'utils';

type Values = Pick<
  CurrencyExchange,
  | 'assetCurrencyId'
  | 'fiatCurrencyId'
  | 'source'
  | 'enabled'
  | 'showInWidget'
  | 'refresh'
  | 'defaultExchange'
  | 'name'
> &
  Partial<
    Pick<
      CurrencyExchange,
      | 'price'
      | 'binancePayTypes'
      | 'bybitPayment'
      | 'payment'
      | 'deviationPercentage'
      | 'deviationAbsolute'
      | 'amount'
      | 'minAmount'
      | 'advOrderCount'
      | 'recentAdvOrderCount'
      | 'deviationFiatCurrencyExchangeId'
      | 'fluctuationMinutes'
      | 'fluctuationPercentage'
      | 'minLimitAmount'
      | 'maxLimitAmount'
      | 'minLimitsSpreadPercentage'
      | 'verified'
      | 'direction'
      | 'skipOrders'
      | 'takeOrders'
      | 'arithmetic'
      | 'hybridExchangeIds'
      | 'availableOrders'
    >
  >;

export const CurrencyExchangeDetailsPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isTechOperator } = useUser();

  // TODO: move tKeys
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.currency_exchange_details',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const {
    fiatCurrenciesOptions,
    assetCurrenciesOptions,
    getCurrencyExchangeOptions,
  } = useCurrencies();
  const { crossFiatCurrencyExchangeOptions } = useCrossFiatCurrencyExchange();

  const isNew = useMemo(() => id === NEW_ID, [id]);

  const [initialValues, setInitialValues] = useState<Values>({
    assetCurrencyId: '',
    fiatCurrencyId: '',
    name: '',
    price: 0,
    source: CurrencyExchangeSource.Bybit,
    enabled: true,
    showInWidget: true,
    refresh: true,
    defaultExchange: false,
    binancePayTypes: '',
    bybitPayment: '',
    payment: '',
    deviationPercentage: 0,
    deviationAbsolute: 0,
    amount: null,
    minAmount: null,
    advOrderCount: null,
    deviationFiatCurrencyExchangeId: '',
    fluctuationMinutes: null,
    fluctuationPercentage: null,
    verified: false,
    arithmetic: false,
    availableOrders: true,
    hybridExchangeIds: [],
    direction: '' as CurrencyExchangeDirection,
  });

  const queryResult = useQuery<CurrencyExchange, AxiosError>(
    [QueryKey, id],
    () => currencyExchangeApi.getOne(id as string),
    {
      enabled: !isNew,
      onSuccess: (item) => {
        setInitialValues({
          assetCurrencyId: item.assetCurrencyId,
          fiatCurrencyId: item.fiatCurrencyId,
          name: item.name,
          price: item.price,
          source: item.source,
          enabled: item.enabled,
          showInWidget: item.showInWidget,
          refresh: item.refresh,
          defaultExchange: item.defaultExchange,
          binancePayTypes: item.binancePayTypes || '',
          bybitPayment: item.bybitPayment || '',
          payment: item.payment || '',
          deviationPercentage: item.deviationPercentage,
          deviationAbsolute: item.deviationAbsolute,
          amount: item.amount,
          minAmount: item.minAmount,
          advOrderCount: item.advOrderCount,
          recentAdvOrderCount: item.recentAdvOrderCount,
          deviationFiatCurrencyExchangeId:
            item.deviationFiatCurrencyExchangeId || '',
          fluctuationMinutes: item.fluctuationMinutes,
          fluctuationPercentage: item.fluctuationPercentage,
          minLimitAmount: item.minLimitAmount,
          maxLimitAmount: item.maxLimitAmount,
          minLimitsSpreadPercentage: item.minLimitsSpreadPercentage,
          verified: !!item.verified,
          direction: item.direction || ('' as CurrencyExchangeDirection),
          skipOrders: item.skipOrders,
          takeOrders: item.takeOrders,
          arithmetic: !!item.arithmetic,
          availableOrders: !!item.availableOrders,
          hybridExchangeIds: item.hybridExchangeIds,
        });
      },
    },
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape(
        {
          assetCurrencyId: Yup.string().required(tCommon('errors.required')),
          fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
          name: Yup.string().required(tCommon('errors.required')),
          source: Yup.mixed<CurrencyExchangeSource>()
            .oneOf(Object.values(CurrencyExchangeSource))
            .required(tCommon('errors.required')),
          price: Yup.number().when('refresh', {
            is: false,
            then: (schema) =>
              schema
                .moreThan(0, tCommon('errors.natural_number'))
                .required(tCommon('errors.required')),
          }),
          binancePayTypes: Yup.string(),
          bybitPayment: Yup.string(),
          payment: Yup.string(),
          enabled: Yup.boolean().required(tCommon('errors.required')),
          showInWidget: Yup.boolean().required(tCommon('errors.required')),
          refresh: Yup.boolean().required(tCommon('errors.required')),
          defaultExchange: Yup.boolean().required(tCommon('errors.required')),
          deviationPercentage: Yup.number()
            .moreThan(-100, tCommon('errors.percentage_absolute'))
            .lessThan(100, tCommon('errors.percentage_absolute')),
          deviationAbsolute: Yup.number().optional(),
          amount: Yup.number().optional().nullable(),
          minAmount: Yup.number().optional().nullable(),
          advOrderCount: Yup.number().optional().nullable(),
          recentAdvOrderCount: Yup.number().optional().nullable(),
          deviationFiatCurrencyExchangeId: Yup.string().optional(),
          fluctuationMinutes: Yup.number()
            .positive(tCommon('errors.natural_number'))
            .when('fluctuationPercentage', ([fluctuationPercentage], schema) =>
              fluctuationPercentage
                ? schema
                    .typeError(tCommon('errors.required'))
                    .required(tCommon('errors.required'))
                : schema.optional().nullable(),
            ),
          fluctuationPercentage: Yup.number()
            .positive(tCommon('errors.natural_number'))
            .when('fluctuationMinutes', ([fluctuationMinutes], schema) =>
              fluctuationMinutes
                ? schema
                    .typeError(tCommon('errors.required'))
                    .required(tCommon('errors.required'))
                : schema.optional().nullable(),
            ),
          minLimitAmount: Yup.number().optional().nullable(),
          maxLimitAmount: Yup.number().optional().nullable(),
          minLimitsSpreadPercentage: Yup.number()
            .optional()
            .nullable()
            .min(0, tCommon('errors.percentage'))
            .lessThan(100, tCommon('errors.percentage')),
          verified: Yup.boolean(),
          direction: Yup.mixed<CurrencyExchangeDirection>()
            .oneOf(Object.values(CurrencyExchangeDirection))
            .optional()
            .nullable(),
          skipOrders: Yup.number().optional().nullable(),
          takeOrders: Yup.number().optional().nullable(),
          arithmetic: Yup.boolean(),
          availableOrders: Yup.boolean(),
          hybridExchangeIds: Yup.array()
            .of(Yup.string().required(tCommon('errors.required')))
            .nullable()
            .notRequired()
            .when('source', {
              is: CurrencyExchangeSource.Hybrid,
              then: (schema) => schema.min(1, tCommon('errors.required')),
            }),
        },
        [['fluctuationMinutes', 'fluctuationPercentage']],
      ),
    [tCommon],
  );
  const { mutate: createCurrency } = useMutation<
    CurrencyExchange,
    AxiosError,
    Partial<CurrencyExchange>,
    unknown
  >(currencyExchangeApi.create);

  const { mutate: updateCurrency } = useMutation<
    CurrencyExchange,
    AxiosError,
    { id: string; data: Partial<CurrencyExchange> },
    unknown
  >(currencyExchangeApi.update);

  const sourceOptions = useMemo(
    () =>
      map(CurrencyExchangeSource, (status) => ({
        value: status,
        label: currencyExchangeUtils.getCurrencyExchangeSourceLabel(status),
      })),
    [],
  );

  const backUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.CURRENCIES;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.CURRENCIES;
    }
  }, [isAdmin, isTechOperator]);

  const onSubmit = useCallback(
    (values: Values, { setSubmitting, setErrors }: FormikHelpers<Values>) => {
      const data = {
        ...values,
        amount: values.amount || null,
        minAmount: values.minAmount || null,
        advOrderCount: values.advOrderCount || null,
      };
      const options = {
        onSuccess: () => {
          queryClient.invalidateQueries(QueryKey.CurrencyExchangeRates);
          navigate(backUrl!);
        },
        onSettled: () => {
          setSubmitting(false);
        },
        onError: (error: AxiosError) => {
          setErrors(validationUtils.getFormErrors(error));
        },
      };
      if (isNew) {
        createCurrency(data, options);
      } else {
        updateCurrency({ id, data }, options);
      }
    },
    [isNew, queryClient, backUrl, navigate, createCurrency, updateCurrency, id],
  );

  const handleSubmit = useCallback(
    (values: Values, helpers: FormikHelpers<Values>) => {
      onSubmit(values, helpers);
    },
    [onSubmit],
  );

  const isSources = useCallback(
    (formik: FormikProps<Values>, sources: CurrencyExchangeSource[]) =>
      sources.includes(formik.values.source),
    [],
  );

  const getHybridCurrencyExchangeOptions = useCallback(
    (formik: FormikProps<Values>) => {
      if (!formik.values.fiatCurrencyId || !formik.values.assetCurrencyId) {
        return [];
      }
      const options = getCurrencyExchangeOptions({
        assetCurrencyId: formik.values.assetCurrencyId,
        fiatCurrencyId: formik.values.fiatCurrencyId,
      });
      return isNew ? options : filter(options, (option) => option.value !== id);
    },
    [getCurrencyExchangeOptions, isNew, id],
  );

  const onSourceChange = useCallback(
    (formik: FormikProps<Values>, source: CurrencyExchangeSource) => {
      if (
        source === CurrencyExchangeSource.Hybrid ||
        !isEmpty(formik.values.hybridExchangeIds)
      ) {
        formik.setFieldValue('hybridExchangeIds', []);
      }
      if (source === CurrencyExchangeSource.Manual) {
        formik.setFieldValue('refresh', false);
      }
    },
    [],
  );

  return (
    <Fragment>
      <PageHeader title={t('title')}></PageHeader>
      <DataWrapper queryResult={isNew ? undefined : queryResult}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <div className="tw-max-w-xs">
                <FormControls>
                  <FormikYesNoRadioGroup
                    label={t('fields.enabled')}
                    name="enabled"
                  />
                  <FormikYesNoRadioGroup
                    label={t('fields.showInWidget')}
                    name="showInWidget"
                  />
                  <FormikTextField
                    label={t('fields.name')}
                    name="name"
                    required
                  />
                  <FormikSelect
                    label={t('fields.source')}
                    name="source"
                    options={sourceOptions}
                    onChange={(value) =>
                      onSourceChange(formik, value as CurrencyExchangeSource)
                    }
                  />
                  {isSources(formik, [CurrencyExchangeSource.Binance]) && (
                    <FormikTextField
                      label={t('fields.payment')}
                      name="binancePayTypes"
                      helperText={t('fields.payment_helper')}
                    />
                  )}
                  {isSources(formik, [CurrencyExchangeSource.Bybit]) && (
                    <FormikTextField
                      label={t('fields.payment')}
                      name="bybitPayment"
                      helperText={t('fields.payment_helper')}
                    />
                  )}
                  {isSources(formik, [CurrencyExchangeSource.HTX]) && (
                    <FormikTextField
                      label={t('fields.payment')}
                      name="payment"
                      helperText={t('fields.payment_helper')}
                    />
                  )}
                  <FormikSelect
                    label={t('fields.asset_currency')}
                    name="assetCurrencyId"
                    options={assetCurrenciesOptions}
                    disabled={!isNew}
                  />
                  <FormikSelect
                    label={t('fields.fiat_currency')}
                    name="fiatCurrencyId"
                    options={fiatCurrenciesOptions}
                    disabled={!isNew}
                  />
                  {!isSources(formik, [
                    CurrencyExchangeSource.Hybrid,
                    CurrencyExchangeSource.Manual,
                  ]) && (
                    <Fragment>
                      <FormikNumericField
                        name="deviationPercentage"
                        defaultValue={0}
                        label={t('fields.deviation_percentage')}
                        percentageSuffix
                      />
                      <FormikNumericField
                        name="deviationAbsolute"
                        defaultValue={0}
                        label={t('fields.deviation_absolute')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoTooltip
                                tooltip={t('fields.deviation_absolute_tooltip')}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormikSelect
                        label={t('fields.deviation_fiat_currency_exchange')}
                        name="deviationFiatCurrencyExchangeId"
                        options={crossFiatCurrencyExchangeOptions}
                        helperText={t(
                          'fields.deviation_fiat_currency_exchange_description',
                        )}
                        noneOption
                      />
                    </Fragment>
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Binance,
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Rapira,
                    CurrencyExchangeSource.ABCEX,
                    CurrencyExchangeSource.Grinex,
                  ]) && (
                    <FormikNumericField
                      label={t('fields.amount')}
                      name="amount"
                      allowNegative={false}
                    />
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Binance,
                    CurrencyExchangeSource.HTX,
                  ]) && (
                    <FormikNumericField
                      label={t('fields.min_amount')}
                      name="minAmount"
                      allowNegative={false}
                    />
                  )}
                  {isSources(formik, [CurrencyExchangeSource.Bybit]) && (
                    <Fragment>
                      <FormikNumericField
                        label={t('fields.adv_order_count')}
                        name="advOrderCount"
                        allowNegative={false}
                        defaultValue={0}
                      />
                    </Fragment>
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Binance,
                  ]) && (
                    <FormikNumericField
                      label={t('fields.recent_adv_order_count')}
                      name="recentAdvOrderCount"
                      allowNegative={false}
                      defaultValue={0}
                    />
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Binance,
                    CurrencyExchangeSource.HTX,
                  ]) && (
                    <Fragment>
                      <FormikYesNoRadioGroup
                        label={t('fields.available_orders')}
                        name="availableOrders"
                      />
                    </Fragment>
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Rapira,
                    CurrencyExchangeSource.ABCEX,
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Binance,
                    CurrencyExchangeSource.HTX,
                    CurrencyExchangeSource.Grinex,
                    CurrencyExchangeSource.Mosca,
                  ]) && (
                    <Fragment>
                      <CurrencyExchangeDirectionSelect
                        name="direction"
                        helperText={`${tCommon(
                          'common.default',
                        )}: ${currencyExchangeUtils.getCurrencyExchangeDirectionLabel(
                          CurrencyExchangeDirection.Buy,
                        )}`}
                      />
                    </Fragment>
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Rapira,
                    CurrencyExchangeSource.ABCEX,
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Binance,
                    CurrencyExchangeSource.HTX,
                    CurrencyExchangeSource.Grinex,
                  ]) && (
                    <Fragment>
                      <FormikNumericField
                        label={t('fields.skip_orders')}
                        name="skipOrders"
                        allowNegative={false}
                        decimalScale={0}
                        defaultValue={0}
                      />
                      <FormikNumericField
                        label={t('fields.take_orders')}
                        name="takeOrders"
                        allowNegative={false}
                        decimalScale={0}
                        defaultValue={0}
                      />
                      <FormikYesNoRadioGroup
                        label={t('fields.arithmetic')}
                        name="arithmetic"
                      />
                    </Fragment>
                  )}
                  {isSources(formik, [
                    CurrencyExchangeSource.Bybit,
                    CurrencyExchangeSource.Binance,
                    CurrencyExchangeSource.HTX,
                  ]) && (
                    <Fragment>
                      <div>
                        <Divider sx={{ mb: 4 }}>
                          {t('fields.limits_range')}
                        </Divider>
                        <FormControls row>
                          <FormikNumericField
                            label={tCommon('common.min')}
                            name="minLimitAmount"
                            allowNegative={false}
                            defaultValue={0}
                          />
                          <FormikNumericField
                            label={tCommon('common.max')}
                            name="maxLimitAmount"
                            allowNegative={false}
                            defaultValue={0}
                          />
                        </FormControls>
                      </div>
                      <FormikNumericField
                        label={t('fields.min_limits_spread')}
                        name="minLimitsSpreadPercentage"
                        allowNegative={false}
                        percentageSuffix
                        defaultValue={0}
                      />
                      <FormikYesNoRadioGroup
                        label={t('fields.verified')}
                        name="verified"
                      />
                    </Fragment>
                  )}
                  {isSources(formik, [CurrencyExchangeSource.Hybrid]) && (
                    <FormikMultiSelect
                      label={t('fields.currency_exchanges')}
                      name="hybridExchangeIds"
                      required
                      options={getHybridCurrencyExchangeOptions(formik)}
                    />
                  )}
                  <FormikYesNoRadioGroup
                    label={t('fields.refresh')}
                    name="refresh"
                    disabled={isSources(formik, [
                      CurrencyExchangeSource.Manual,
                    ])}
                  />
                  <FormikNumericField
                    label={t('fields.price')}
                    name="price"
                    allowNegative={false}
                    required
                    disabled={formik.values.refresh}
                  />
                  <FormikYesNoRadioGroup
                    label={t('fields.default_exchange')}
                    name="defaultExchange"
                  />
                  {!isSources(formik, [CurrencyExchangeSource.Manual]) && (
                    <Fragment>
                      <div>
                        <Typography variant="h6">
                          {t('fields.fluctuation')}
                        </Typography>
                        <Typography variant="caption" color="GrayText">
                          {t('fields.fluctuation_helper')}
                        </Typography>
                      </div>
                      <FormikNumericField
                        label={t('fields.fluctuation_percentage')}
                        name="fluctuationPercentage"
                        allowNegative={false}
                        defaultValue={null}
                      />
                      <FormikNumericField
                        label={t('fields.fluctuation_minutes')}
                        name="fluctuationMinutes"
                        decimalScale={0}
                        allowNegative={false}
                        defaultValue={null}
                      />
                    </Fragment>
                  )}
                </FormControls>
                <div className="tw-mt-4">
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    {tCommon('buttons.save')}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </DataWrapper>
    </Fragment>
  );
};
